import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  ArrowRight, PlayCircle, Users, FileText, MonitorPlay, Target, DollarSign, Flag,
  ShieldCheck, ClipboardList, FileCheck2, Network, Maximize2, Headphones,
  CheckCircle2, XCircle, ChevronDown, Mail, Phone, Building2, User,
} from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import AmbientParticles from "@/components/site/AmbientParticles";
import IllustrativePill from "@/components/site/IllustrativePill";
import { BOOK_DEMO_IMAGES } from "@/lib/site";

const STEPS = [
  { n: "01", icon: Users,       title: "Discovery Call",         desc: "We learn about your organization, challenges, and objectives." },
  { n: "02", icon: FileText,    title: "Current Workflow Review", desc: "We assess your systems, processes, and data landscape." },
  { n: "03", icon: MonitorPlay, title: "Platform Demonstration",  desc: "See Esthera RealityOS in action with your real use cases." },
  { n: "04", icon: Target,      title: "Use Case Mapping",        desc: "We map solutions to your highest impact opportunities." },
  { n: "05", icon: DollarSign,  title: "ROI Discussion",          desc: "We model potential outcomes and financial impact." },
  { n: "06", icon: Flag,        title: "Implementation Strategy", desc: "We outline a tailored roadmap for your success." },
];

const CHALLENGES = [
  "Disconnected systems and data silos",
  "Manual processes and spreadsheet chaos",
  "Limited visibility and outdated reporting",
  "Reactive decisions and missed opportunities",
  "High operational costs and inefficiencies",
];

const FUTURES = [
  "Unified data on one intelligent platform",
  "AI-powered insights and automation",
  "Real-time executive visibility",
  "Proactive decisions and pipeline growth",
  "Lower costs with higher performance",
];

const ENTERPRISE = [
  { icon: ShieldCheck,   title: "Enterprise Security",  desc: "Bank-grade encryption, access controls, and threat protection." },
  { icon: ClipboardList, title: "Governance",           desc: "Role-based access, audit trails, and policy enforcement." },
  { icon: FileCheck2,    title: "Compliance",           desc: "SOC 2, ISO 27001, and industry best practice aligned." },
  { icon: Network,       title: "Integrations",         desc: "Connect 100+ systems and your existing technology stack." },
  { icon: Maximize2,     title: "Scalability",          desc: "Built to scale with your business and support global operations." },
  { icon: Headphones,    title: "Dedicated Support",    desc: "White-glove onboarding and ongoing customer success." },
];

function SectionTitle({ children, testid }) {
  return (
    <h2 data-testid={testid}
      className="font-display text-center text-platinum text-[30px] sm:text-[36px] lg:text-[44px] leading-tight tracking-[-0.018em] fade-up">
      {children}
    </h2>
  );
}

export default function BookDemoPage() {
  const navigate = useNavigate();
  const [scrollY, setScrollY] = useState(0);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", orgType: "", company: "", companySize: "", phone: "", message: "" });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, []);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setScrollY(window.scrollY));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  const heroParallax = `translate3d(0, ${-scrollY * 0.14}px, 0)`;

  // Field-level validation. Returns error string or empty string.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validateField = (name, value) => {
    const v = (value || "").trim();
    if (name === "phone") {
      if (!v) return "Phone number is required.";
      if (!isValidPhoneNumber(v)) return "Please enter a valid phone number.";
      return "";
    }
    if (!v) {
      switch (name) {
        case "firstName":   return "First name is required.";
        case "lastName":    return "Last name is required.";
        case "email":       return "Email is required.";
        case "orgType":     return "Organization type is required.";
        case "company":     return "Company is required.";
        case "companySize": return "Company size is required.";
        case "message":     return "A short message is required.";
        default: return "This field is required.";
      }
    }
    if (name === "email" && !EMAIL_RE.test(v)) return "Please enter a valid email address.";
    return "";
  };

  const errors = Object.fromEntries(
    Object.keys(form).map((k) => [k, validateField(k, form[k])])
  );
  const isValid = Object.values(errors).every((e) => !e);

  // Show error inline only after user has touched the field OR after a submit attempt.
  const showErr = (name) => (touched[name] || touched.__all__) && errors[name];

  const setField = (name, value) => setForm((f) => ({ ...f, [name]: value }));
  const markTouched = (name) => setTouched((t) => ({ ...t, [name]: true }));

  const submit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      setTouched({ __all__: true });
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/demo-requests`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          full_name: `${form.firstName} ${form.lastName}`.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          role: form.orgType,
          team_size: form.companySize,
          phone: form.phone,
          message: form.message.trim(),
        }),
      });
      if (!res.ok) throw new Error(`Request failed (${res.status})`);
      setSubmitted(true);
    } catch (err) {
      setSubmitError("We couldn't send your request. Please try again in a moment, or email us directly at hello@estherarealityos.com.");
    } finally {
      setSubmitting(false);
    }
  };

  const fieldStyle = "w-full h-12 rounded-md bg-[rgba(10,15,26,0.7)] border border-white/10 px-4 text-[14px] text-platinum placeholder:text-platinum/40 focus:outline-none focus:border-sapphire-glow/55 transition-colors";
  const fieldErrStyle = "border-red-500/60 focus:border-red-400/70";
  const cls = (name, extra = "") => `${fieldStyle} ${showErr(name) ? fieldErrStyle : ""} ${extra}`;
  const fieldErr = (name) => showErr(name) ? (
    <div className="text-[11.5px] text-red-400/90 mt-1 pl-0.5" data-testid={`err-${name}`}>{errors[name]}</div>
  ) : null;
  const reqStar = <span className="text-red-400/70 ml-0.5" aria-hidden>*</span>;

  return (
    <div className="min-h-screen bg-obsidian text-platinum relative overflow-x-hidden" data-testid="book-demo-page">
      <div className="fixed inset-0 pointer-events-none z-0" aria-hidden>
        <AmbientParticles count={34} tone="mixed" density={0.55} />
      </div>
      <Navbar />

      {/* HERO */}
      <section className="relative pt-[120px] lg:pt-[150px] pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none bg-grid opacity-[0.06]" aria-hidden />
        <div className="absolute -top-32 right-0 w-[820px] h-[820px] pointer-events-none"
          style={{ background: "radial-gradient(closest-side, rgba(96,165,250,0.20), transparent)" }} aria-hidden />
        <div className="relative mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-[1fr_1.3fr] gap-8 lg:gap-12 items-center min-h-[560px] lg:min-h-[660px]">
            <div className="fade-up relative z-10">
              <div className="eyebrow-positioning">EXECUTIVE DEMONSTRATION
              </div>
              <h1 className="font-display mt-5 text-platinum text-[44px] sm:text-[54px] lg:text-[60px] xl:text-[64px] leading-[1.04] tracking-[-0.022em]">
                See How Modern<br />Real Estate<br />Runs On{" "}
                <span className="text-sapphire-glow" style={{ textShadow: "0 0 32px rgba(96,165,250,0.42)" }}>Intelligence</span>
              </h1>
              <p className="mt-6 text-[16px] sm:text-[17px] text-platinum/72 leading-[1.7] max-w-[520px]">
                A personalized walkthrough of Esthera RealityOS<br />
                tailored to your organization, workflows,<br />
                and growth objectives.
              </p>
              <div className="mt-9 flex flex-wrap items-center gap-3">
                <a href="#demo-form" data-testid="hero-book-exec-demo"
                  className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2">
                  Book Executive Demo <ArrowRight className="w-4 h-4" />
                </a>
                <button onClick={() => navigate("/platform")} data-testid="hero-watch-overview"
                  className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center gap-2">
                  <PlayCircle className="w-4 h-4" /> Watch Overview
                </button>
              </div>
            </div>

            <div className="relative lg:-mr-10 xl:-mr-20" data-testid="book-demo-hero-visual"
              style={{ transform: heroParallax, willChange: "transform" }}>
              <div className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(60% 55% at 55% 50%, rgba(37,99,235,0.32), transparent 72%)", filter: "blur(48px)" }} aria-hidden />
              <div className="relative">
                <IllustrativePill position="top-4 right-4" z="z-20" />
                <img src={BOOK_DEMO_IMAGES.hero}
                  alt="Esthera RealityOS Executive Dashboard — sample pipeline, active opportunities, AI recommendations, market intelligence"
                  className="block w-full h-[520px] lg:h-[680px] object-cover object-center"
                  style={{
                    WebkitMaskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                    maskImage: "radial-gradient(120% 110% at 50% 50%, #000 60%, rgba(0,0,0,0.6) 80%, transparent 100%)",
                  }}
                  draggable="false" />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.75) 0%, rgba(10,15,26,0) 18%, rgba(10,15,26,0) 70%, rgba(10,15,26,0.98) 100%)" }} aria-hidden />
                <div className="absolute inset-0 pointer-events-none"
                  style={{ background: "linear-gradient(90deg, rgba(10,15,26,1) 0%, rgba(10,15,26,0.82) 14%, rgba(10,15,26,0.18) 32%, rgba(10,15,26,0) 50%, rgba(10,15,26,0) 78%, rgba(10,15,26,0.55) 100%)" }} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT HAPPENS DURING THE DEMO — 6-step timeline */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionTitle testid="demo-process-title">What Happens During The Demo</SectionTitle>
          <div className="mt-14 relative">
            {/* Connector line — desktop only, behind icons */}
            <div className="hidden lg:block absolute top-[26px] left-[8%] right-[8%] h-px pointer-events-none"
              style={{ background: "linear-gradient(90deg, transparent, rgba(217,165,79,0.45) 6%, rgba(217,165,79,0.45) 94%, transparent)" }} aria-hidden />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-y-10 gap-x-4 relative">
              {STEPS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={s.n} data-testid={`demo-step-${i}`} className="text-center fade-up" style={{ animationDelay: `${i * 90}ms` }}>
                    <div className="flex items-center justify-center gap-3 mb-4">
                      <div className="relative w-[52px] h-[52px] rounded-full inline-flex items-center justify-center shrink-0"
                        style={{
                          background: "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.14), #0E1422 80%)",
                          border: "1px solid rgba(96,165,250,0.55)",
                          boxShadow: "0 0 18px -3px rgba(96,165,250,0.45), inset 0 0 12px rgba(96,165,250,0.10)",
                        }}>
                        <Icon className="w-5 h-5" style={{ color: "#60A5FA" }} />
                      </div>
                      <div className="font-display text-amber-300 text-[26px] tracking-tight"
                        style={{ textShadow: "0 0 14px rgba(217,165,79,0.55)" }}>{s.n}</div>
                    </div>
                    <div className="font-display text-platinum text-[15px] sm:text-[16px] leading-[1.25] tracking-tight px-2">{s.title}</div>
                    <p className="mt-2 text-[12px] text-platinum/60 leading-[1.6] px-2 max-w-[180px] mx-auto">{s.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CHALLENGE vs FUTURE — split comparison */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="grid lg:grid-cols-2 gap-5 lg:gap-6">
            {/* Challenge */}
            <div className="relative rounded-2xl overflow-hidden border border-white/[0.07] p-7 lg:p-9 fade-up min-h-[420px]"
              style={{ background: "linear-gradient(180deg, rgba(20,12,12,0.55) 0%, rgba(10,15,26,0.92) 100%)" }}>
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-red-400/40 to-transparent" />
              <h3 className="font-display text-platinum text-[24px] sm:text-[28px] lg:text-[32px] leading-[1.1] tracking-[-0.018em]">
                The Challenge Today
              </h3>
              <div className="grid grid-cols-[1fr_1fr] gap-6 mt-7 items-center">
                <ul className="space-y-4">
                  {CHALLENGES.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-[13.5px] text-platinum/75 leading-[1.55]">
                      <XCircle className="w-4 h-4 mt-[3px] text-red-400/85 shrink-0" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
                <div className="relative aspect-square">
                  <div className="absolute inset-[-6%] pointer-events-none"
                    style={{ background: "radial-gradient(closest-side, rgba(248,113,113,0.18), transparent 70%)", filter: "blur(30px)" }} aria-hidden />
                  <img src={BOOK_DEMO_IMAGES.challenge} alt="Disconnected systems, data silos, manual reports — fragmented landscape with red warnings"
                    className="relative w-full h-full object-contain"
                    style={{
                      filter: "brightness(0.95) contrast(1.18) saturate(1.05)",
                      WebkitMaskImage: "radial-gradient(96% 96% at 50% 50%, #000 60%, rgba(0,0,0,0.5) 82%, transparent 100%)",
                      maskImage: "radial-gradient(96% 96% at 50% 50%, #000 60%, rgba(0,0,0,0.5) 82%, transparent 100%)",
                    }}
                    draggable="false" />
                </div>
              </div>
            </div>

            {/* Future */}
            <div className="relative rounded-2xl overflow-hidden border border-white/[0.07] p-7 lg:p-9 fade-up min-h-[420px]"
              style={{ background: "linear-gradient(180deg, rgba(13,22,42,0.55) 0%, rgba(10,15,26,0.92) 100%)", animationDelay: "120ms" }}>
              <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/55 to-transparent" />
              <h3 className="font-display text-platinum text-[24px] sm:text-[28px] lg:text-[32px] leading-[1.1] tracking-[-0.018em]">
                The Future With <span className="text-sapphire-glow" style={{ textShadow: "0 0 28px rgba(96,165,250,0.45)" }}>Esthera RealityOS</span>
              </h3>
              <div className="grid grid-cols-[1fr_1fr] gap-6 mt-7 items-center">
                <ul className="space-y-4">
                  {FUTURES.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-[13.5px] text-platinum/80 leading-[1.55]">
                      <CheckCircle2 className="w-4 h-4 mt-[3px] text-sapphire-glow shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="relative aspect-square">
                  <div className="absolute inset-[-6%] pointer-events-none"
                    style={{ background: "radial-gradient(closest-side, rgba(37,99,235,0.40), transparent 72%)", filter: "blur(36px)" }} aria-hidden />
                  <img src={BOOK_DEMO_IMAGES.future} alt="Unified intelligence — connected city skyline with AI, real-time visibility, automation, smarter decisions, scalable growth"
                    className="relative w-full h-full object-contain"
                    style={{
                      filter: "brightness(0.95) contrast(1.30) saturate(1.18)",
                      WebkitMaskImage: "radial-gradient(96% 96% at 50% 50%, #000 60%, rgba(0,0,0,0.5) 82%, transparent 100%)",
                      maskImage: "radial-gradient(96% 96% at 50% 50%, #000 60%, rgba(0,0,0,0.5) 82%, transparent 100%)",
                    }}
                    draggable="false" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ENTERPRISE READY — 6 trust cards */}
      <section className="relative pb-16 lg:pb-20">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <SectionTitle testid="enterprise-ready-title">Enterprise Ready. Built For What Matters.</SectionTitle>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 lg:gap-5">
            {ENTERPRISE.map((e, i) => {
              const Icon = e.icon;
              return (
                <div key={e.title} data-testid={`enterprise-ready-${i}`}
                  className="text-center fade-up" style={{ animationDelay: `${i * 70}ms` }}>
                  <div className="w-12 h-12 rounded-full inline-flex items-center justify-center mb-4"
                    style={{
                      background: "radial-gradient(circle at 30% 30%, rgba(96,165,250,0.12), #0E1422 75%)",
                      border: "1px solid rgba(96,165,250,0.55)",
                      boxShadow: "0 0 18px -3px rgba(96,165,250,0.45), inset 0 0 10px rgba(96,165,250,0.10)",
                    }}>
                    <Icon className="w-5 h-5" style={{ color: "#60A5FA" }} />
                  </div>
                  <div className="font-display text-platinum text-[14.5px] sm:text-[15px] leading-[1.22] tracking-tight">{e.title}</div>
                  <p className="mt-2 text-[12px] text-platinum/60 leading-[1.6] px-1">{e.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REQUEST PERSONALIZED DEMO — boardroom image + form */}
      <section className="relative pb-16 lg:pb-20" id="demo-form">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="relative rounded-2xl border border-white/[0.07] overflow-hidden"
            style={{ background: "linear-gradient(180deg, rgba(13,18,32,0.55) 0%, rgba(10,15,26,0.92) 100%)" }}>
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/55 to-transparent" />
            <div className="grid lg:grid-cols-[0.85fr_1fr_1.05fr] gap-6 lg:gap-10 p-6 sm:p-10 lg:p-12 items-center">
              {/* Boardroom image */}
              <div className="relative aspect-[4/5] lg:aspect-[3/4] rounded-xl overflow-hidden fade-up">
                <div className="absolute inset-0 pointer-events-none z-10"
                  style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.10) 0%, transparent 40%, rgba(10,15,26,0.65) 100%)" }} aria-hidden />
                <img src={BOOK_DEMO_IMAGES.boardroom} alt="Executive presentation of Esthera RealityOS Portfolio Performance Overview to enterprise leaders"
                  className="block w-full h-full object-cover object-center"
                  style={{ filter: "brightness(0.92) contrast(1.10) saturate(1.04)" }}
                  draggable="false" />
              </div>

              {/* Bullet list */}
              <div className="fade-up" style={{ animationDelay: "80ms" }}>
                <h3 className="font-display text-platinum text-[24px] sm:text-[28px] lg:text-[32px] leading-[1.12] tracking-[-0.018em]">
                  Request Your <span className="text-sapphire-glow" style={{ textShadow: "0 0 28px rgba(96,165,250,0.45)" }}>Personalized Demo</span>
                </h3>
                <ul className="mt-6 space-y-3.5">
                  {[
                    "Personalized platform walkthrough",
                    "Tailored use-case discussion",
                    "Implementation recommendations",
                    "Q&A with product specialists",
                  ].map((b) => (
                    <li key={b} className="flex items-start gap-3 text-[13.5px] sm:text-[14px] text-platinum/80 leading-[1.55]">
                      <CheckCircle2 className="w-4 h-4 mt-[3px] text-amber-300 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-7 inline-flex items-start gap-3 p-4 rounded-lg"
                  style={{ background: "rgba(13,22,42,0.55)", border: "1px solid rgba(96,165,250,0.22)" }}>
                  <ShieldCheck className="w-4 h-4 mt-[2px] text-sapphire-glow shrink-0" />
                  <div>
                    <div className="text-[12.5px] text-platinum font-medium">No commitment. Just insights.</div>
                    <div className="text-[11.5px] text-platinum/55 mt-0.5">Our team will confirm your demo within one business day.</div>
                  </div>
                </div>
              </div>

              {/* FORM / SUCCESS */}
              {submitted ? (
                <div className="fade-up" data-testid="demo-success" style={{ animationDelay: "80ms" }}>
                  <div className="w-14 h-14 rounded-full inline-flex items-center justify-center border border-sapphire-glow/50 breathe-glow"
                    style={{ background: "radial-gradient(closest-side, rgba(37,99,235,0.35), rgba(37,99,235,0.05) 70%, transparent 90%)" }}>
                    <CheckCircle2 className="w-7 h-7 text-sapphire-glow" strokeWidth={1.7} />
                  </div>
                  <h3 className="mt-5 font-display text-platinum text-[26px] sm:text-[30px] leading-[1.15] tracking-[-0.015em]">
                    Request received — <span className="text-sapphire-glow" style={{ textShadow: "0 0 22px rgba(96,165,250,0.35)" }}>thanks, {form.firstName || "there"}.</span>
                  </h3>
                  <p className="mt-3 text-[14px] sm:text-[15px] text-platinum/70 leading-[1.7] max-w-[520px]">
                    Someone from our team will follow up with you shortly at <span className="text-platinum">{form.email}</span> to confirm a time. If you&rsquo;d like to jump ahead, book a live 30-minute demo directly on our calendar below.
                  </p>

                  <div className="mt-6 relative rounded-xl border border-sapphire-glow/25 overflow-hidden"
                    style={{ background: "linear-gradient(135deg, rgba(37,99,235,0.14) 0%, rgba(13,30,62,0.55) 55%, rgba(10,15,26,0.92) 100%)" }}
                    data-testid="demo-cal-card">
                    <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-sapphire-glow/60 to-transparent" />
                    <div className="p-5 sm:p-6">
                      <div className="flex items-center gap-2 text-[10.5px] tracking-[0.28em] uppercase text-sapphire-glow font-semibold">
                        <span className="w-1.5 h-1.5 rounded-full bg-sapphire-glow" style={{ boxShadow: "0 0 8px rgba(96,165,250,0.85)" }} />
                        Demo Call · 30 minutes
                      </div>
                      <div className="mt-2.5 text-[15px] sm:text-[16px] text-platinum leading-[1.55]">
                        A focused walkthrough of Esthera RealityOS, built around how you&rsquo;d actually use it. See the platform live and get your questions answered directly.
                      </div>
                      <a href="https://cal.com/esthera/30min" target="_blank" rel="noopener noreferrer"
                        data-testid="demo-cal-cta"
                        className="btn-primary mt-5 rounded-md h-11 px-5 text-[13.5px] font-medium inline-flex items-center gap-2">
                        Schedule My Demo <ArrowRight className="w-4 h-4" />
                      </a>
                    </div>
                  </div>

                  <div className="mt-5 text-[12px] text-platinum/45">
                    Prefer email? Reach us anytime at <a href="mailto:hello@estherarealityos.com" className="text-sapphire-glow hover:text-white transition-colors">hello@estherarealityos.com</a>.
                  </div>
                </div>
              ) : (
              <form className="fade-up space-y-3.5" onSubmit={submit} data-testid="demo-form" style={{ animationDelay: "160ms" }} noValidate>
                <div className="grid grid-cols-2 gap-3.5">
                  <div>
                    <div className="relative">
                      <input className={cls("firstName")} placeholder="e.g. Priya" required
                        value={form.firstName}
                        onChange={(e) => setField("firstName", e.target.value)}
                        onBlur={() => markTouched("firstName")}
                        data-testid="form-first-name" />
                      <User className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-platinum/35 pointer-events-none" />
                    </div>
                    {fieldErr("firstName")}
                  </div>
                  <div>
                    <input className={cls("lastName")} placeholder="e.g. Sharma" required
                      value={form.lastName}
                      onChange={(e) => setField("lastName", e.target.value)}
                      onBlur={() => markTouched("lastName")}
                      data-testid="form-last-name" />
                    {fieldErr("lastName")}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3.5">
                  <div>
                    <div className="relative">
                      <input className={cls("email")} type="email" placeholder="you@company.com" required
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        onBlur={() => markTouched("email")}
                        data-testid="form-email" />
                      <Mail className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-platinum/35 pointer-events-none" />
                    </div>
                    {fieldErr("email")}
                  </div>
                  <div>
                    <div className="relative">
                      <select className={cls("orgType", "appearance-none pr-10")} required
                        value={form.orgType}
                        onChange={(e) => { setField("orgType", e.target.value); markTouched("orgType"); }}
                        onBlur={() => markTouched("orgType")}
                        data-testid="form-org-type">
                        <option value="" disabled>Organization type</option>
                        <option value="Brokerage">Brokerage</option>
                        <option value="Developer">Developer</option>
                        <option value="Investor">Investor</option>
                        <option value="Property Team">Property Team</option>
                        <option value="Enterprise">Enterprise</option>
                      </select>
                      <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-platinum/50 pointer-events-none" />
                    </div>
                    {fieldErr("orgType")}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3.5">
                  <div>
                    <div className="relative">
                      <input className={cls("company")} placeholder="e.g. Sotheby's International Realty" required
                        value={form.company}
                        onChange={(e) => setField("company", e.target.value)}
                        onBlur={() => markTouched("company")}
                        data-testid="form-company" />
                      <Building2 className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-platinum/35 pointer-events-none" />
                    </div>
                    {fieldErr("company")}
                  </div>
                  <div>
                    <div className="relative">
                      <select className={cls("companySize", "appearance-none pr-10")} required
                        value={form.companySize}
                        onChange={(e) => { setField("companySize", e.target.value); markTouched("companySize"); }}
                        onBlur={() => markTouched("companySize")}
                        data-testid="form-company-size">
                        <option value="" disabled>Team size</option>
                        <option value="1-50">1–50</option>
                        <option value="51-200">51–200</option>
                        <option value="201-1000">201–1,000</option>
                        <option value="1000+">1,000+</option>
                      </select>
                      <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-platinum/50 pointer-events-none" />
                    </div>
                    {fieldErr("companySize")}
                  </div>
                </div>
                <div>
                  <PhoneInput
                    defaultCountry="IN"
                    international
                    countryCallingCodeEditable={false}
                    placeholder="98765 43210"
                    value={form.phone}
                    onChange={(v) => setField("phone", v || "")}
                    onBlur={() => markTouched("phone")}
                    className={`esthera-phone-input ${showErr("phone") ? "err" : ""}`}
                    data-testid="form-phone" />
                  {fieldErr("phone")}
                </div>
                <div>
                  <textarea rows={3}
                    className={cls("message", "!h-auto py-3 resize-none")}
                    placeholder="What are you hoping to solve? e.g. Cutting lead response time on our Dubai luxury portfolio."
                    required
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                    onBlur={() => markTouched("message")}
                    data-testid="form-message" />
                  {fieldErr("message")}
                </div>

                {submitError && (
                  <div data-testid="form-error" role="alert"
                    className="rounded-md px-3.5 py-2.5 text-[13px] leading-[1.5] flex items-start gap-2"
                    style={{ background: "rgba(220,38,38,0.10)", border: "1px solid rgba(220,38,38,0.35)", color: "#FCA5A5" }}>
                    <XCircle className="w-4 h-4 mt-[2px] shrink-0" />
                    <span>{submitError}</span>
                  </div>
                )}

                <button type="submit" disabled={submitting} data-testid="form-submit"
                  className="btn-primary w-full rounded-md h-12 text-[14px] font-medium inline-flex items-center justify-center gap-2 disabled:opacity-70">
                  {submitting ? "Scheduling…" : <>Schedule My Demo <ArrowRight className="w-4 h-4" /></>}
                </button>
                <p className="text-[11px] text-platinum/45 leading-relaxed">
                  Fields marked <span className="text-red-400/70">*</span> are required.
                </p>
              </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* TRANSFORM CTA */}
      <section className="relative pb-20 lg:pb-24" data-testid="transform-cta">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10">
          <div className="relative rounded-2xl overflow-hidden border border-white/[0.08] min-h-[300px] lg:min-h-[340px]"
            style={{ background: "linear-gradient(135deg, rgba(13,30,62,0.55) 0%, rgba(10,15,26,0.92) 60%, rgba(10,15,26,0.95) 100%)" }}>
            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-amber-300/55 to-transparent" />

            {/* Bottom skyline image — final sapphire + gold mesh network */}
            <div className="absolute inset-0 pointer-events-none">
              <img src={BOOK_DEMO_IMAGES.ctaSkyline} alt="" aria-hidden="true"
                className="absolute inset-0 w-full h-full object-cover object-center"
                style={{
                  filter: "brightness(0.78) contrast(1.18) saturate(1.20)",
                  WebkitMaskImage: "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,0.95) 100%)",
                  maskImage: "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.85) 40%, rgba(0,0,0,0.95) 100%)",
                }}
                draggable="false" />
              {/* Soft horizontal vignette so copy on the left stays readable */}
              <div className="absolute inset-0"
                style={{ background: "linear-gradient(90deg, rgba(10,15,26,0.88) 0%, rgba(10,15,26,0.55) 38%, rgba(10,15,26,0.18) 70%, rgba(10,15,26,0.05) 100%)" }} />
              {/* Top edge feather into the section above */}
              <div className="absolute top-0 inset-x-0 h-24 pointer-events-none"
                style={{ background: "linear-gradient(180deg, rgba(10,15,26,0.85), transparent)" }} aria-hidden />
            </div>

            <div className="relative z-10 grid lg:grid-cols-[1.4fr_1fr] gap-10 items-center p-10 sm:p-14 lg:p-16">
              <div className="fade-up">
                <h3 className="font-display text-platinum text-[28px] sm:text-[34px] lg:text-[40px] leading-[1.08] tracking-[-0.018em]">
                  Transform The Way<br />
                  <span className="text-sapphire-glow" style={{ textShadow: "0 0 30px rgba(96,165,250,0.45)" }}>You Operate Real Estate</span>
                </h3>
                <p className="mt-5 text-[14px] sm:text-[15px] text-platinum/72 leading-[1.7] max-w-[520px]">
                  Join the leaders who are unifying data, automating workflows, and driving measurable growth.
                </p>
              </div>
              <div className="flex flex-col gap-3 fade-up" style={{ animationDelay: "120ms" }}>
                <a href="#demo-form" data-testid="transform-cta-schedule"
                  className="btn-primary rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center justify-between gap-2 w-full">
                  Schedule Demo <ArrowRight className="w-4 h-4" />
                </a>
                <button onClick={() => navigate("/platform")} data-testid="transform-cta-explore"
                  className="btn-ghost rounded-md h-12 px-6 text-[14px] font-medium inline-flex items-center justify-between gap-2 w-full">
                  Explore Platform <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
